import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TablePagination } from "@/components/ui/table-pagination";
import {
  History,
  RotateCw,
  Search,
  Eye,
  Hash,
  Package,
} from "lucide-react";
import { formatDateTime, cn } from "@/lib/utils";
import TransactionDetailsModal from "./TransactionDetailsModal";

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "receipt", label: "Receipt (GRN)" },
  { value: "sale", label: "Sale" },
  { value: "adjustment", label: "Adjustment" },
  { value: "transfer_in", label: "Transfer In" },
  { value: "transfer_out", label: "Transfer Out" },
  { value: "return", label: "Return" },
  { value: "count", label: "Stock Count" },
];

const TYPE_BADGE_VARIANTS = {
  receipt: "success",
  sale: "info",
  adjustment: "warning",
  transfer_in: "success",
  transfer_out: "destructive",
  return: "secondary",
  count: "outline",
};

const REFERENCE_LABELS = {
  goods_received_note_item: "GRN Item",
  goods_received_note: "GRN",
  sale_item: "Sale Item",
  sale: "Sale",
  stock_adjustment: "Adjustment",
  stock_adjustment_item: "Adjustment Item",
  repair_part: "Repair Part",
  stock_transfer: "Transfer",
};

export const ProductTransactions = ({ productId, currencySymbol = "$" }) => {
  const currentStore = useSelector((state) => state.current_store);
  const storeId = currentStore?.id || null;
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const canReadInventory = permissions.includes("inventory read");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typeFilter, setTypeFilter] = useState("");
  const [searchText, setSearchText] = useState("");

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Fetch transactions limited to this product & current store (10 per page default)
  const {
    data: transactionsData,
    isLoading,
    isFetching,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: [
      "product-inventory-transactions",
      productId,
      storeId,
      currentPage,
      pageSize,
      typeFilter,
    ],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.TRANSACTIONS, {
        params: {
          product_id: productId,
          store_id: storeId || undefined,
          type: typeFilter || null,
          page: currentPage,
          per_page: pageSize,
          with: "inventory,user",
        },
      });
      return res.data;
    },
    enabled: !!productId && canReadInventory,
  });

  const rawTransactions = useMemo(
    () => transactionsData?.data || [],
    [transactionsData],
  );

  // 1. Resolve inventory ID: from existing transactions or by-product endpoint
  const directInventoryId = useMemo(() => {
    return rawTransactions.find((tx) => tx.inventory_id)?.inventory_id || null;
  }, [rawTransactions]);

  const { data: storeInventoryId } = useQuery({
    queryKey: ["inventory-by-product", productId, storeId],
    queryFn: async () => {
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.INVENTORY.BY_PRODUCT(productId),
        );
        const inventories = Array.isArray(res.data?.data)
          ? res.data.data
          : res.data?.data
            ? [res.data.data]
            : [];
        const match = storeId
          ? inventories.find((inv) => String(inv.store_id) === String(storeId))
          : inventories[0];
        return match?.id || null;
      } catch {
        return null;
      }
    },
    enabled: !!productId && !directInventoryId && canReadInventory,
  });

  const activeInventoryId = directInventoryId || storeInventoryId || null;

  // 2. Fetch inventory transactions summary
  const {
    data: summaryData,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["inventory-summary", activeInventoryId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.TRANSACTIONS_SUMMARY(activeInventoryId),
      );
      return res.data?.data || null;
    },
    enabled: !!activeInventoryId && canReadInventory,
  });

  const summaryMetrics = useMemo(() => {
    if (!summaryData) return null;
    const byType = summaryData.by_type || {};

    const totalIn =
      (byType.receipt?.total_quantity || 0) +
      (byType.transfer_in?.total_quantity || 0) +
      (byType.return?.total_quantity || 0) +
      (byType.opening_stock?.total_quantity || 0);

    const inCount =
      (byType.receipt?.transaction_count || 0) +
      (byType.transfer_in?.transaction_count || 0) +
      (byType.return?.transaction_count || 0) +
      (byType.opening_stock?.transaction_count || 0);

    const totalOut = Math.abs(
      (byType.sale?.total_quantity || 0) +
      (byType.transfer_out?.total_quantity || 0),
    );

    const outCount =
      (byType.sale?.transaction_count || 0) +
      (byType.transfer_out?.transaction_count || 0);

    const netAdjustments =
      (byType.adjustment?.total_quantity || 0) +
      (byType.count?.total_quantity || 0);

    const adjCount =
      (byType.adjustment?.transaction_count || 0) +
      (byType.count?.transaction_count || 0);

    return {
      currentStock: summaryData.current_quantity ?? 0,
      totalTransactions: summaryData.total_transactions ?? 0,
      totalIn,
      totalOut,
      netAdjustments,
      inCount,
      outCount,
      adjCount,
    };
  }, [summaryData]);

  const handleRefresh = () => {
    refetchTransactions();
    if (activeInventoryId) {
      refetchSummary();
    }
  };

  const filteredTransactions = useMemo(() => {
    if (!searchText) return rawTransactions;
    const term = searchText.toLowerCase().trim();
    return rawTransactions.filter((tx) => {
      const notes = (tx.notes || "").toLowerCase();
      const refId = (tx.reference_id || "").toLowerCase();
      const hasSerial = tx.meta?.serial_numbers?.some((s) =>
        String(s).toLowerCase().includes(term),
      );
      return notes.includes(term) || refId.includes(term) || hasSerial;
    });
  }, [rawTransactions, searchText]);

  const isClientFiltered = Boolean(searchText);
  const totalCount =
    transactionsData?.meta?.total ||
    transactionsData?.total ||
    rawTransactions.length;

  const effectiveTotal = isClientFiltered
    ? filteredTransactions.length
    : totalCount;

  const effectiveTotalPages = Math.max(
    1,
    transactionsData?.meta?.last_page ||
    Math.ceil(effectiveTotal / pageSize) ||
    1,
  );

  const displayTransactions = isClientFiltered
    ? filteredTransactions.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    )
    : filteredTransactions;

  const handleOpenDetails = (tx) => {
    setSelectedTransaction(tx);
    setIsDetailModalOpen(true);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (!canReadInventory) return null;

  return (
    <Card className="shadow-lg border-border/60 overflow-hidden bg-card">
      <div className="h-1 bg-primary" />
      <CardHeader className="bg-muted/50 pb-5 border-b border-border/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 text-primary rounded-lg">
              <History className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Inventory Transactions</CardTitle>
              <CardDescription>
                Audit history of receipts, sales, and stock adjustments for this product
                {currentStore?.name ? ` at ${currentStore.name}` : ""}
              </CardDescription>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-40">
              <ReactSelect
                options={TYPE_OPTIONS}
                value={
                  TYPE_OPTIONS.find((o) => o.value === typeFilter) ||
                  TYPE_OPTIONS[0]
                }
                onChange={(opt) => {
                  setTypeFilter(opt ? opt.value : "");
                  setCurrentPage(1);
                }}
                placeholder="All Types"
                className="text-xs"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              className="h-10 px-3 text-xs gap-1.5"
            >
              <RotateCw size={14} className={cn(isFetching && "animate-spin")} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-5 space-y-4">
        {/* Quick Inventory Summary Cards */}
        {summaryMetrics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
            <div className="p-3 rounded-lg border bg-card/60 flex flex-col justify-between">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Current Stock
              </span>
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-xl font-bold text-foreground">
                  {summaryMetrics.currentStock}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {summaryMetrics.totalTransactions} events
                </span>
              </div>
            </div>

            <div className="p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/20 flex flex-col justify-between">
              <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                Total Inflow
              </span>
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  +{summaryMetrics.totalIn}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {summaryMetrics.inCount} in
                </span>
              </div>
            </div>

            <div className="p-3 rounded-lg border bg-rose-500/5 border-rose-500/20 flex flex-col justify-between">
              <span className="text-[11px] font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider">
                Total Outflow
              </span>
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-xl font-bold text-rose-600 dark:text-rose-400">
                  -{summaryMetrics.totalOut}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {summaryMetrics.outCount} out
                </span>
              </div>
            </div>

            <div className="p-3 rounded-lg border bg-amber-500/5 border-amber-500/20 flex flex-col justify-between">
              <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                Adjustments
              </span>
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-xl font-bold text-amber-600 dark:text-amber-400">
                  {summaryMetrics.netAdjustments > 0
                    ? `+${summaryMetrics.netAdjustments}`
                    : summaryMetrics.netAdjustments}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {summaryMetrics.adjCount} adj
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Search & Meta Bar */}
        <div className="flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-3.5 w-3.5 absolute left-3 top-3 text-muted-foreground" />
            <Input
              placeholder="Search by notes, reference ID, serial..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 h-9 text-xs"
            />
          </div>

          <div className="text-xs text-muted-foreground font-medium">
            Total Movements:{" "}
            <span className="text-foreground font-semibold">
              {effectiveTotal}
            </span>
          </div>
        </div>

        {/* Transactions Table */}
        {isLoading ? (
          <div className="space-y-2 py-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-12 bg-muted/40 animate-pulse rounded-md"
              />
            ))}
          </div>
        ) : displayTransactions.length > 0 ? (
          <div className="rounded-lg border border-border/70 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 border-b border-border/60 text-muted-foreground font-medium uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Date & Time</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Reference</th>
                  <th className="px-4 py-3 text-right">Quantity</th>
                  <th className="px-4 py-3 text-right">Unit Cost</th>
                  <th className="px-4 py-3 text-right">Total Value</th>
                  <th className="px-4 py-3 text-center">Serials</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {displayTransactions.map((tx) => {
                  const itemType = tx.type || tx.transaction_type;
                  const badgeVariant =
                    TYPE_BADGE_VARIANTS[itemType] || "secondary";
                  const refLabel =
                    REFERENCE_LABELS[tx.reference_type] ||
                    (tx.reference_type
                      ? tx.reference_type.replace(/_/g, " ")
                      : "-");
                  const serialCount = tx.meta?.serial_numbers?.length || 0;
                  const unitCost =
                    tx.unit_cost !== null && tx.unit_cost !== undefined
                      ? parseFloat(tx.unit_cost)
                      : null;
                  const totalValue =
                    tx.total_value !== null && tx.total_value !== undefined
                      ? parseFloat(tx.total_value)
                      : null;

                  return (
                    <tr
                      key={tx.id}
                      className="hover:bg-muted/30 transition-colors group cursor-pointer"
                      onClick={() => handleOpenDetails(tx)}
                    >
                      {/* Date & Time */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-foreground">
                          {formatDateTime(tx.created_at)}
                        </div>
                      </td>

                      {/* Type Badge */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge
                          variant={badgeVariant}
                          className="capitalize text-[11px] font-medium"
                        >
                          {itemType || "movement"}
                        </Badge>
                      </td>

                      {/* Reference */}
                      <td className="px-4 py-3 max-w-[220px]">
                        <div className="font-medium text-foreground capitalize truncate">
                          {refLabel}
                        </div>
                        {tx.notes && (
                          <div
                            className="text-[11px] text-muted-foreground truncate"
                            title={tx.notes}
                          >
                            &quot;{tx.notes}&quot;
                          </div>
                        )}
                      </td>

                      {/* Quantity with old->new transition */}
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <span
                          className={`font-bold font-mono text-sm ${tx.quantity > 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : tx.quantity < 0
                                ? "text-rose-600 dark:text-rose-400"
                                : "text-muted-foreground"
                            }`}
                        >
                          {tx.quantity > 0 ? `+${tx.quantity}` : tx.quantity}
                        </span>
                        {tx.meta?.old_quantity !== undefined &&
                          tx.meta?.new_quantity !== undefined && (
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {tx.meta.old_quantity} → {tx.meta.new_quantity}
                            </div>
                          )}
                      </td>

                      {/* Unit Cost */}
                      <td className="px-4 py-3 text-right whitespace-nowrap font-mono text-muted-foreground">
                        {unitCost !== null && !isNaN(unitCost) && unitCost > 0
                          ? `${currencySymbol} ${unitCost.toFixed(2)}`
                          : "-"}
                      </td>

                      {/* Total Value */}
                      <td className="px-4 py-3 text-right whitespace-nowrap font-mono font-medium text-foreground">
                        {totalValue !== null &&
                          !isNaN(totalValue) &&
                          totalValue > 0
                          ? `${currencySymbol} ${totalValue.toFixed(2)}`
                          : "-"}
                      </td>

                      {/* Serials */}
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        {serialCount > 0 ? (
                          <Badge
                            variant="secondary"
                            className="text-[10px] h-5 px-1.5 font-mono gap-1"
                          >
                            <Hash size={10} />
                            {serialCount}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDetails(tx);
                                }}
                              >
                                <Eye size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>View transaction details</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center border rounded-lg bg-muted/10">
            <Package className="h-8 w-8 text-muted-foreground/60 mb-2" />
            <p className="text-sm font-medium text-foreground">
              No transactions recorded
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-sm">
              Stock movements, receipts, and sales for this product will appear here.
            </p>
          </div>
        )}

        {/* Standard Paginated Table Footer (10 per page default) */}
        <div className="border-t pt-2">
          <TablePagination
            currentPage={currentPage}
            totalPages={effectiveTotalPages}
            pageSize={pageSize}
            totalItems={effectiveTotal}
            itemLabel="movements"
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            showPageSize={true}
            showQuickJumper={false}
            showPageNumbers={true}
          />
        </div>
      </CardContent>

      {/* Details Modal */}
      <TransactionDetailsModal
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        transaction={selectedTransaction}
        currencySymbol={currencySymbol}
      />
    </Card>
  );
};

ProductTransactions.propTypes = {
  productId: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string,
};

export default ProductTransactions;
